import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Phone } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact Us" },
  { to: "/feedback", label: "Feedback" },
];

const SERVE_LINKS = [
  { to: "/office-vending", label: "Offices" },
  { to: "/apartment-building-vending", label: "Apartment Buildings" },
  { to: "/medical-office-vending", label: "Medical Offices" },
  { to: "/service-area", label: "Service Area" },
];

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="group flex items-center">
    <img
      src="/brand/logo-light.360w.8d5f5d91.webp"
      width={360}
      height={101}
      alt="Vortex Vending Services LLC"
      className="h-10 w-auto transition-transform duration-300 group-hover:scale-[1.03]"
    />
  </Link>
);

const SiteNav = () => {
  const [open, setOpen] = useState(false);
  const [serveOpen, setServeOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-white/5 bg-vortex-ink/70 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Logo />
        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative font-mono text-xs tracking-[0.2em] uppercase transition-colors duration-300 hover:text-vortex-cyan ${
                  isActive ? "text-vortex-cyan" : "text-slate-400"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div
            className="relative"
            onMouseEnter={() => setServeOpen(true)}
            onMouseLeave={() => setServeOpen(false)}
          >
            <button
              data-testid="nav-serve-toggle"
              onClick={() => setServeOpen(!serveOpen)}
              className={`flex items-center gap-1.5 font-mono text-xs tracking-[0.2em] uppercase transition-colors duration-300 hover:text-vortex-cyan ${
                serveOpen ? "text-vortex-cyan" : "text-slate-400"
              }`}
            >
              Who We Serve
              <ChevronDown size={14} className={`transition-transform duration-300 ${serveOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {serveOpen && (
                <motion.div
                  data-testid="nav-serve-menu"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute right-0 top-full mt-4 w-60 border border-white/10 bg-vortex-ink/95 p-3 backdrop-blur-xl"
                >
                  {SERVE_LINKS.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      data-testid={`nav-serve-${l.to.slice(1)}`}
                      onClick={() => setServeOpen(false)}
                      className={({ isActive }) =>
                        `block px-4 py-2.5 font-mono text-[11px] tracking-[0.15em] uppercase transition-colors duration-300 ${
                          isActive ? "text-vortex-cyan" : "text-slate-400 hover:text-vortex-cyan"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <a
            data-testid="nav-phone-link"
            href="tel:+16109838060"
            className="hidden items-center gap-2 font-mono text-xs tracking-[0.15em] text-slate-200 xl:flex transition-colors duration-300 hover:text-vortex-cyan"
          >
            <Phone size={14} className="text-vortex-cyan" />
            (610) 983-8060
          </a>
          <button
            data-testid="nav-cta-button"
            onClick={() => navigate("/contact")}
            className="rounded-full border border-vortex-cyan/40 px-6 py-2.5 font-mono text-xs tracking-[0.2em] uppercase text-vortex-cyan transition-all duration-300 hover:bg-vortex-cyan hover:text-vortex-ink hover:shadow-[0_0_30px_rgba(92,179,226,0.4)]"
          >
            Get a Cooler
          </button>
        </nav>
        <div className="flex items-center gap-5 lg:hidden">
          <a
            data-testid="nav-mobile-call-button"
            href="tel:+16109838060"
            aria-label="Call (610) 983-8060"
            className="flex items-center gap-2 rounded-full bg-vortex-cyan px-4 py-2 font-mono text-[11px] tracking-[0.15em] uppercase text-vortex-ink"
          >
            <Phone size={14} />
            Call
          </a>
          <button
            data-testid="nav-mobile-toggle"
            className="text-slate-200"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/5 bg-vortex-ink/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-6 px-6 py-8">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-heading text-2xl font-bold text-slate-200 transition-colors hover:text-vortex-cyan"
                >
                  {l.label}
                </NavLink>
              ))}
              <p className="mt-4 font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">Who We Serve</p>
              {SERVE_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-serve-${l.to.slice(1)}`}
                  className="font-heading text-lg font-bold text-slate-300 transition-colors hover:text-vortex-cyan"
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                data-testid="nav-mobile-cta-button"
                onClick={() => { setOpen(false); navigate("/contact"); }}
                className="mt-2 rounded-full border border-vortex-cyan/40 px-6 py-3 font-mono text-xs tracking-[0.2em] uppercase text-vortex-cyan"
              >
                Get a Cooler
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default SiteNav;
